import { useEffect, useState } from "react";
import { fetchShopifyProducts } from "@/lib/shopify";
import type { ShopifyProduct } from "@/lib/shopify";
import ShopifyProductCard from "@/components/ShopifyProductCard";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";

const Shop = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const { edges, pageInfo } = await fetchShopifyProducts(20);
        setProducts(edges.map(p => p.node));
        setHasNextPage(pageInfo.hasNextPage);
        setEndCursor(pageInfo.endCursor);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  const loadMoreProducts = async () => {
    if (!hasNextPage || !endCursor || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      const { edges, pageInfo } = await fetchShopifyProducts(20, endCursor);
      setProducts(prev => [...prev, ...edges.map(p => p.node)]);
      setHasNextPage(pageInfo.hasNextPage);
      setEndCursor(pageInfo.endCursor);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-white" dir="rtl">
        <Header />
        <main className="py-20 px-4">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 animate-fade-in">
              <h1 className="text-3xl font-light mb-3 tracking-wide" style={{ fontWeight: 300 }}>המוצרים שלנו</h1>
              <div className="w-12 h-px bg-primary/40 mx-auto"></div>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square bg-muted mb-4" />
                    <div className="h-3 bg-muted mb-2" />
                    <div className="h-3 bg-muted w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-24 animate-fade-in">
                <p className="text-muted-foreground text-base mb-2" style={{ fontWeight: 300 }}>
                  עדיין אין מוצרים בחנות
                </p>
                <p className="text-sm text-muted-foreground/70" style={{ fontWeight: 300 }}>
                  הוסף מוצרים דרך Shopify Admin כדי להתחיל למכור
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {products.map((product, index) => (
                    <div 
                      key={product.id} 
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'backwards' }}
                    >
                      <ShopifyProductCard product={product} />
                    </div>
                  ))}
                </div>
                
                {hasNextPage && (
                  <div className="flex justify-center mt-12">
                    <Button
                      onClick={loadMoreProducts}
                      disabled={isLoadingMore}
                      className="bg-primary/90 hover:bg-primary text-white px-12 py-6"
                      style={{ fontWeight: 300 }}
                    >
                      {isLoadingMore ? 'טוען...' : 'טען עוד מוצרים'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Shop;
